import { FormEvent, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const FORM_ID = "db70dpf891islr12uev0";
const FORM_ADDR = "https://readdy.ai/api/form/db70dpf891islr12uev0";

const TEAM = [
  { name: "顾清和", role: "内容主编", desc: "负责剧情解析与深度解读方向，长期关注家庭题材剧集。" },
  { name: "沈砚", role: "剧评作者", desc: "专注角色与表演分析，撰写剧集结构拆解类文章。" },
  { name: "周然", role: "资讯编辑", desc: "追踪剧集动态、幕后花絮与官方公告，维护资讯栏目。" },
  { name: "林知夏", role: "资料整理", desc: "负责时间线、人物关系图等资料性内容的整理与校对。" },
];

const SCOPE = [
  { icon: "ri-book-open-line", title: "剧情介绍", desc: "主线、支线与关键节点解析，逐集梳理故事脉络。" },
  { icon: "ri-user-3-line", title: "角色分析", desc: "角色背景、性格特征与人物关系，辅以演员信息。" },
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "收视动态、幕后制作、主创访谈与官方公告。" },
];

export default function About() {
  useDocumentTitle({
    title: "关于本站 - 我妻子的一切内容站",
    description:
      "了解《我妻子的一切》内容站的定位、内容范围与运营团队，并可通过反馈表单与我们联系，共同完善剧集资料。",
    keywords: "关于本站,我妻子的一切,内容说明,联系反馈",
  });

  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const auxValue = String(formData.get("contact_alt") || "").trim();
    if (auxValue) {
      setFormStatus("success");
      setFormError("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    setFormStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && code === "OK") {
        setFormStatus("success");
        setFormMessage("");
        setFormName("");
        setFormEmail("");
      } else if (isSpam) {
        setFormStatus("error");
        setFormError("提交内容被识别为异常数据，请修改后重试。");
      } else {
        setFormStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setFormStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="w-full px-4 py-8 md:px-8 md:py-12">
      <Breadcrumb items={[{ label: "关于本站" }]} />

      <header className="mt-5 max-w-3xl">
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-accent-600">About</p>
        <h1 className="mt-2 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
          关于本站
        </h1>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
          本站是一个专注于电视剧《我妻子的一切》的影视内容分享平台，面向喜欢该剧的观众、影视爱好者与剧集研究者。
          我们希望用扎实、结构化的内容，让每一次观剧之后都有值得回味与讨论的空间。
        </p>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-xl font-semibold text-foreground-950">内容范围说明</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {SCOPE.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className={`${item.icon} text-xl`} />
              </span>
              <h3 className="mt-3 font-heading text-base font-semibold text-foreground-900">
                {item.title}
              </h3>
              <p className="mt-1.5 text-sm leading-6 text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-semibold text-foreground-950">运营团队（示例）</h2>
        <p className="mt-2 text-sm text-foreground-500">以下团队成员及信息均为演示示例，不代表真实联系方式。</p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TEAM.map((member) => (
            <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 font-heading text-base font-semibold text-accent-900">
                  {member.name.slice(0, 1)}
                </span>
                <div>
                  <p className="font-heading text-sm font-semibold text-foreground-950">{member.name}</p>
                  <p className="text-xs text-accent-700">{member.role}</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-6 text-foreground-500">{member.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground-950">联系我们 / 反馈</h2>
          <p className="mt-2 text-sm leading-7 text-foreground-600">
            如果你对内容有建议、发现资料错误，或者想推荐相关话题，欢迎通过下面的表单留言。我们会在看到后尽快处理。
          </p>

          <div className="mt-5 flex flex-col gap-3">
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <i className="ri-mail-line text-lg text-accent-600" />
              <span className="text-sm text-foreground-700">contact@allaboutmywife.demo</span>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <i className="ri-information-line text-lg text-accent-600" />
              <span className="text-sm text-foreground-700">本站为演示站点，邮箱为示例地址</span>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading text-sm font-semibold text-foreground-900">版权声明</h3>
            <p className="mt-2 text-xs leading-6 text-foreground-500">
              本站所有剧情介绍、角色分析与资讯内容均为演示性质的原创示例文本，仅用于展示内容组织方式，
              不构成任何官方发布信息，也不涉及任何影视作品的实际版权内容。
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          {formStatus === "success" ? (
            <div className="flex h-full flex-col items-center justify-center py-10 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-check-line text-2xl" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">感谢你的留言</h3>
              <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-500">
                我们已经收到你的反馈，感谢你帮助我们完善《我妻子的一切》的内容资料。
              </p>
              <button
                type="button"
                onClick={() => setFormStatus("idle")}
                className="mt-5 inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer"
              >
                <i className="ri-edit-line" />
                继续留言
              </button>
            </div>
          ) : (
            <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div>
                <label htmlFor="about-name" className="mb-1.5 block text-sm text-foreground-700">
                  姓名
                </label>
                <input
                  id="about-name"
                  type="text"
                  name="name"
                  value={formName}
                  onChange={(event) => setFormName(event.target.value)}
                  required
                  placeholder="请输入你的称呼"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="about-email" className="mb-1.5 block text-sm text-foreground-700">
                  邮箱
                </label>
                <input
                  id="about-email"
                  type="email"
                  name="email"
                  value={formEmail}
                  onChange={(event) => setFormEmail(event.target.value)}
                  required
                  placeholder="请输入你的邮箱"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="about-message" className="mb-1.5 block text-sm text-foreground-700">
                  留言
                </label>
                <textarea
                  id="about-message"
                  name="message"
                  value={formMessage}
                  onChange={(event) => setFormMessage(event.target.value.slice(0, 500))}
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="说说你的建议或想法（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-6 text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
                <p className="mt-1 text-right text-xs text-foreground-400">{formMessage.length}/500</p>
              </div>

              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="iwm-field-aux"
              />

              {formStatus === "error" && formError && (
                <p className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2 text-xs leading-5 text-accent-900">
                  <i className="ri-error-warning-line mt-0.5 text-base" />
                  {formError}
                </p>
              )}

              <button
                type="submit"
                disabled={formStatus === "submitting"}
                className="inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-700 px-6 text-sm text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                {formStatus === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" />
                    提交留言
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}